import { useMemo } from "react";
import { Bot } from "lucide-react";
import { splitMentions, type User } from "@lumika/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  cn,
} from "@lumika/ui";

/**
 * Renders text that may contain `@[id]` mention tokens — the timeline
 * note/message body, and (Increment 2) task & follow-up bodies.
 *
 * Each token becomes a highlighted, clickable `<MentionChip>`; plain
 * stretches render as-is. A token whose id is not in `users` (a removed
 * teammate, or a stale id) renders as a greyed, non-clickable chip — the
 * raw `@[uuid]` is never shown to a human.
 */
export function MentionedText({
  body,
  users,
  className,
  interactive = true,
}: {
  body: string | null | undefined;
  users: User[];
  className?: string;
  /**
   * When false, resolved mentions render as plain styled spans instead
   * of popover buttons — for bodies nested inside another interactive
   * element (e.g. a clickable notification row), where a nested button
   * is invalid HTML and the row's click would swallow the popover.
   */
  interactive?: boolean;
}) {
  const usersById = useMemo(() => {
    const map = new Map<string, User>();
    for (const u of users) map.set(u.id, u);
    return map;
  }, [users]);

  const segments = useMemo(() => splitMentions(body ?? ""), [body]);

  return (
    <p className={cn("whitespace-pre-wrap break-words", className)}>
      {segments.map((seg, i) =>
        seg.type === "text" ? (
          <span key={i}>{seg.value}</span>
        ) : (
          <MentionChip
            key={i}
            user={usersById.get(seg.id) ?? null}
            interactive={interactive}
          />
        ),
      )}
    </p>
  );
}

/** Best human-readable name for a user, with sensible fallbacks. */
export function userLabel(u: User): string {
  if (u.displayName?.trim()) return u.displayName.trim();
  const full = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  if (full) return full;
  if (u.email?.trim()) return u.email.trim();
  return "teammate";
}

function initialsOf(u: User): string {
  const label = userLabel(u);
  const parts = label.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + second).toUpperCase();
}

/**
 * One inline mention. Resolved → a highlighted button that opens a
 * Slack-style profile popover on click. Unresolved → an inert greyed
 * "removed teammate" chip.
 */
function MentionChip({
  user,
  interactive,
}: {
  user: User | null;
  interactive: boolean;
}) {
  if (!user) {
    return (
      <span className="rounded bg-muted px-1 font-medium text-muted-foreground">
        @removed teammate
      </span>
    );
  }

  const label = userLabel(user);
  const isAi = user.kind === "ai";

  // A resolved mention inherits the surrounding text color (`text-current`)
  // rather than a fixed teal. The old `text-primary` chip vanished on any
  // colored bubble — teal-on-teal on the outbound bubble, teal on the amber
  // internal-note bubble (~1.1:1, the reported bug). Deriving from
  // `currentColor` makes the mention exactly the bubble's own body-text color,
  // so it clears AA on every surface (neutral, amber note, dark teal, AI-green)
  // in BOTH themes by construction. Deliberately no fill: a currentColor tint
  // would *lighten* a dark bubble and drag its light text below AA. The
  // underline + medium weight carry the "this is a mention" affordance; AI
  // mentions add a Bot glyph — a color-independent marker that reads on every
  // surface now that the teal/neutral color split is gone.
  const mentionClass =
    "rounded font-medium text-current underline underline-offset-2";
  const mentionInner = (
    <>
      {isAi ? (
        <Bot aria-hidden className="mr-0.5 inline h-3 w-3 align-[-0.15em]" />
      ) : null}
      @{label}
    </>
  );

  if (!interactive) {
    return <span className={mentionClass}>{mentionInner}</span>;
  }

  const role =
    [user.jobTitle, user.department].filter(Boolean).join(" · ") ||
    (isAi ? "Assistant" : "Team member");

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`View ${label}`}
          className={cn(mentionClass, "hover:decoration-2")}
        >
          {mentionInner}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
              {initialsOf(user)}
            </span>
            <div className="min-w-0">
              <DialogTitle className="truncate">{label}</DialogTitle>
              <DialogDescription className="mt-1">{role}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        {user.email ? (
          <a
            href={`mailto:${user.email}`}
            className="truncate text-sm text-foreground underline underline-offset-2"
          >
            {user.email}
          </a>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
